import { z } from 'zod';
import type {
  FormatConfig as EngineFormatConfig,
  HandicapConfig as EngineHandicapConfig,
  ScoringConfig as EngineScoringConfig,
  StrokePlayRules as EngineStrokePlayRules,
  TiebreakConfig as EngineTiebreakConfig,
} from '@pingo/engine';
import {
  DEFAULT_NEW_PERIO,
  DEFAULT_PERIO,
  DEFAULT_STROKE_PLAY,
  DEFAULT_TIEBREAK,
} from '@pingo/engine';
import { FormatKind, ViewKind } from '../enums';

/**
 * Tournament.scoringConfig.
 *
 * Two shapes:
 *  - STORED (ScoringConfigSchema output, `StoredScoringConfig`): what the admin chose. "Auto" fields
 *    are omitted/undefined (handicap.allowance, rounding, maxHandicap, minHandicap, newPerio,
 *    postForWhs) so they follow the format/method defaults even after the format changes.
 *  - EFFECTIVE (`ScoringConfig` = normalizeScoringConfig(stored)): every field resolved; this is
 *    what the engine and all modules use. api: lib/tournament-config.ts parseScoringConfig() returns
 *    the effective config; GET endpoints return both (`scoringConfig` + `effectiveScoringConfig`).
 */

/** zod mirror of engine FormatConfig (Tournament.scoringConfig.format). */
export const FormatConfigSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('STROKE_GROSS') }),
  z.object({ kind: z.literal('STROKE_NET') }),
  z.object({
    kind: z.literal('STABLEFORD'),
    basis: z.enum(['NET', 'GROSS']).default('NET'),
    pointsTable: z.record(z.string(), z.number()).nullable().optional(),
  }),
  z.object({
    kind: z.literal('SCRAMBLE'),
    teamSize: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    net: z.boolean().default(true),
    allowances: z.array(z.number().min(0).max(1)),
  }),
  z.object({
    kind: z.literal('MATCH_SINGLE'),
    net: z.boolean().default(true),
    allowance: z.number().min(0).max(1).default(1),
  }),
  z.object({
    kind: z.literal('MATCH_FOURSOME'),
    net: z.boolean().default(true),
    allowance: z.number().min(0).max(1).default(0.5),
  }),
  z.object({
    kind: z.literal('MATCH_FOURBALL'),
    net: z.boolean().default(true),
    allowance: z.number().min(0).max(1).default(0.9),
  }),
]);
export type FormatConfig = z.infer<typeof FormatConfigSchema>;

export const NewPerioConfigSchema = z.object({
  hiddenHoleCount: z.int().min(3).max(18).default(DEFAULT_NEW_PERIO.hiddenHoleCount),
  multiplier: z.number().positive().default(DEFAULT_NEW_PERIO.multiplier),
  factor: z.number().positive().default(DEFAULT_NEW_PERIO.factor),
  perHoleCap: z.enum(['NONE', 'DOUBLE_PAR', 'TRIPLE_BOGEY']).default(DEFAULT_NEW_PERIO.perHoleCap),
  selection: z.enum(['RANDOM', 'MANUAL']).default(DEFAULT_NEW_PERIO.selection),
  /** legacy/fallback only: real draws live on RoundCourse.hiddenHoles (one per round layout) */
  hiddenHoles: z.array(z.int().min(1).max(18)).nullable().optional(),
  seed: z.string().nullable().optional(),
});

const HANDICAP_METHODS = ['NONE', 'WHS', 'FIXED', 'NEW_PERIO', 'PERIO', 'CALLAWAY'] as const;
const PERIO_FAMILY = new Set(['NEW_PERIO', 'PERIO', 'CALLAWAY']);

/** STORED handicap config: undefined = automatic default (see normalizeHandicapConfig). */
export const HandicapConfigSchema = z.object({
  method: z.enum(HANDICAP_METHODS).default('NONE'),
  /** undefined = by format: 0.95 individual stroke/stableford, ignored for MATCH_* and SCRAMBLE */
  allowance: z.number().min(0).max(1).optional(),
  /** undefined = auto (Perio family 36, else no cap); null = explicitly no cap */
  maxHandicap: z.number().nullable().optional(),
  /** undefined = auto (Perio family 0, else none); null = explicitly none (plus handicaps allowed) */
  minHandicap: z.number().nullable().optional(),
  /** undefined = auto (Perio family ONE_DECIMAL, else INTEGER) */
  rounding: z.enum(['INTEGER', 'ONE_DECIMAL']).optional(),
  /** undefined/null = DEFAULT_NEW_PERIO (NEW_PERIO) or DEFAULT_PERIO (PERIO) */
  newPerio: NewPerioConfigSchema.nullable().optional(),
  /**
   * Create WHS HandicapRecords from this event's locked rounds. undefined = auto: true for
   * individual formats, false for SCRAMBLE / MATCH_FOURSOME (team scores are not acceptable).
   */
  postForWhs: z.boolean().optional(),
});
export type StoredHandicapConfig = z.infer<typeof HandicapConfigSchema>;

export const TiebreakConfigSchema = z.object({
  method: z.enum(['COUNTBACK', 'SHARE']).default(DEFAULT_TIEBREAK.method),
  segments: z
    .array(z.object({ lastHoles: z.int().min(1).max(18) }))
    .default(DEFAULT_TIEBREAK.segments),
  basis: z.enum(['FORMAT', 'GROSS']).default(DEFAULT_TIEBREAK.basis),
  scope: z.enum(['FINAL_ROUND', 'ALL_ROUNDS']).default(DEFAULT_TIEBREAK.scope),
  holeOrder: z.enum(['CARD', 'PLAYED']).default(DEFAULT_TIEBREAK.holeOrder),
  /** net basis only; never applied to STABLEFORD points or the GROSS basis (CLAUDE.md §8.2) */
  handicapFractions: z.boolean().default(DEFAULT_TIEBREAK.handicapFractions),
  fallback: z
    .enum(['HOLE_BY_HOLE_BACKWARD', 'LOWER_HANDICAP', 'SHARE'])
    .default(DEFAULT_TIEBREAK.fallback),
  playoffPositions: z.array(z.int().min(1)).default([]),
  liveDisplay: z.enum(['TIED', 'COUNTBACK']).default(DEFAULT_TIEBREAK.liveDisplay),
});
export type TiebreakConfig = z.infer<typeof TiebreakConfigSchema>;

export const CutConfigSchema = z.object({
  afterRound: z.int().min(1),
  topN: z.int().min(1),
  includeTies: z.boolean().default(true),
});

/** Stroke play incomplete hole / maximum score (engine StrokePlayRules). */
export const StrokePlayRulesSchema = z.object({
  incompleteHole: z.enum(['DQ', 'MAX_SCORE']).default(DEFAULT_STROKE_PLAY.incompleteHole),
  maxScore: z
    .enum(['NONE', 'DOUBLE_PAR', 'NET_DOUBLE_BOGEY', 'PAR_PLUS_N'])
    .default(DEFAULT_STROKE_PLAY.maxScore),
  maxScoreN: z.int().min(1).max(10).nullable().default(null),
});
export type StrokePlayRules = z.infer<typeof StrokePlayRulesSchema>;

/**
 * 분류별 시상 (category award rankings). Defaults below; CategoryDefinition.awardConfig overrides
 * per category (CategoryAwardOverrideSchema).
 *  view: which leaderboard view decides category places (null = primary format)
 *  places: prize places per option (default 3)
 *  excludeOverallTop: the overall top N (of `view`) are removed from category rankings (0 = none)
 *  allowDuplicateAwards: false = a competitor wins at most one category prize (first category in
 *    sortOrder wins), true = may win one per category
 */
export const CategoryAwardConfigSchema = z.object({
  view: z.union([FormatKind, ViewKind]).nullable().default(null),
  places: z.int().min(1).max(50).default(3),
  excludeOverallTop: z.int().min(0).max(50).default(0),
  allowDuplicateAwards: z.boolean().default(false),
});
export type CategoryAwardConfig = z.infer<typeof CategoryAwardConfigSchema>;
export const CategoryAwardOverrideSchema = z.object({
  view: z.union([FormatKind, ViewKind]).nullable().optional(),
  places: z.int().min(1).max(50).optional(),
  excludeOverallTop: z.int().min(0).max(50).optional(),
  allowDuplicateAwards: z.boolean().optional(),
});
export type CategoryAwardOverride = z.infer<typeof CategoryAwardOverrideSchema>;

/** Tournament.scoringConfig (STORED shape). Impossible format x method combinations are rejected. */
export const ScoringConfigSchema = z
  .object({
    format: FormatConfigSchema,
    additionalViews: z.array(ViewKind).default([]),
    handicap: HandicapConfigSchema.default({ method: 'NONE' }),
    tiebreak: TiebreakConfigSchema.default({
      ...DEFAULT_TIEBREAK,
      segments: [...DEFAULT_TIEBREAK.segments],
      playoffPositions: [],
    }),
    cut: CutConfigSchema.nullable().optional(),
    strokePlay: StrokePlayRulesSchema.default({ ...DEFAULT_STROKE_PLAY, maxScoreN: null }),
    categoryAwards: CategoryAwardConfigSchema.default({
      view: null,
      places: 3,
      excludeOverallTop: 0,
      allowDuplicateAwards: false,
    }),
  })
  .superRefine((c, ctx) => {
    const kind = c.format.kind;
    const team = kind === 'SCRAMBLE' || kind.startsWith('MATCH_');
    if (team && PERIO_FAMILY.has(c.handicap.method)) {
      ctx.addIssue({
        code: 'custom',
        path: ['handicap', 'method'],
        message: `${c.handicap.method} is not allowed with ${kind} (use WHS, FIXED or NONE)`,
      });
    }
    if (c.strokePlay.incompleteHole === 'MAX_SCORE' && c.strokePlay.maxScore === 'NONE') {
      ctx.addIssue({
        code: 'custom',
        path: ['strokePlay', 'maxScore'],
        message: 'incompleteHole MAX_SCORE requires a maxScore rule',
      });
    }
    if (c.strokePlay.maxScore === 'PAR_PLUS_N' && !c.strokePlay.maxScoreN) {
      ctx.addIssue({
        code: 'custom',
        path: ['strokePlay', 'maxScoreN'],
        message: 'maxScore PAR_PLUS_N requires maxScoreN',
      });
    }
    if (c.format.kind === 'SCRAMBLE' && c.format.allowances.length !== c.format.teamSize) {
      ctx.addIssue({
        code: 'custom',
        path: ['format', 'allowances'],
        message: 'SCRAMBLE allowances must have one entry per team member',
      });
    }
  });
export type StoredScoringConfig = z.infer<typeof ScoringConfigSchema>;
export type ScoringConfigInput = z.input<typeof ScoringConfigSchema>;

/** Effective handicap config (engine HandicapConfig + PinGo extras). */
export type HandicapConfig = EngineHandicapConfig & { postForWhs: boolean };

/** Effective scoring config: what every module and the engine use. */
export type ScoringConfig = Omit<EngineScoringConfig, 'handicap' | 'strokePlay'> & {
  handicap: HandicapConfig;
  strokePlay: StrokePlayRules;
  categoryAwards: CategoryAwardConfig;
};

/** Default playing-handicap allowance by format (WHS Appendix C) for the WHS method. */
export function defaultAllowanceFor(kind: FormatKind): number {
  switch (kind) {
    case 'STROKE_NET':
    case 'STROKE_GROSS':
    case 'STABLEFORD':
      return 0.95;
    default:
      return 1; // MATCH_* / SCRAMBLE: their own allowances apply instead
  }
}

export function normalizeHandicapConfig(
  h: StoredHandicapConfig,
  formatKind: FormatKind,
): HandicapConfig {
  const perio = PERIO_FAMILY.has(h.method);
  const newPerio =
    h.method === 'NEW_PERIO'
      ? (h.newPerio ?? { ...DEFAULT_NEW_PERIO })
      : h.method === 'PERIO'
        ? (h.newPerio ?? { ...DEFAULT_PERIO })
        : null;
  return {
    method: h.method,
    allowance: h.allowance ?? defaultAllowanceFor(formatKind),
    maxHandicap: h.maxHandicap !== undefined ? h.maxHandicap : perio ? 36 : null,
    minHandicap: h.minHandicap !== undefined ? h.minHandicap : perio ? 0 : null,
    rounding: h.rounding ?? (perio ? 'ONE_DECIMAL' : 'INTEGER'),
    newPerio,
    postForWhs: h.postForWhs ?? !(formatKind === 'SCRAMBLE' || formatKind === 'MATCH_FOURSOME'),
  };
}

/** STORED -> EFFECTIVE (pure; idempotent on already-effective configs). */
export function normalizeScoringConfig(c: StoredScoringConfig): ScoringConfig {
  return {
    ...c,
    handicap: normalizeHandicapConfig(c.handicap, c.format.kind),
  };
}

/** Parse raw JSON (DB column / request body) and return the EFFECTIVE config. */
export function parseEffectiveScoringConfig(raw: unknown): ScoringConfig {
  return normalizeScoringConfig(ScoringConfigSchema.parse(raw));
}

/** Manual playoff ordering: { [viewKey]: competitorIds[] } (Tournament.manualOrder) */
export const ManualOrderSchema = z.record(z.string(), z.array(z.string()));
export type ManualOrder = z.infer<typeof ManualOrderSchema>;

/**
 * Playoff holes of a view's manual order live under the reserved key `playoff:<view>` as hole
 * numbers in text (["18", "1"]): the JSON stays a valid ManualOrder record, so readers that only
 * look up `<view>` are unaffected (no schema change).
 */
export const MANUAL_ORDER_PLAYOFF_PREFIX = 'playoff:';

export function manualOrderPlayoffKey(view: string): string {
  return `${MANUAL_ORDER_PLAYOFF_PREFIX}${view}`;
}

/** Recorded playoff holes of a view (valid hole numbers only, playing order). */
export function manualOrderPlayoffHoles(order: ManualOrder, view: string): number[] {
  return (order[manualOrderPlayoffKey(view)] ?? [])
    .map((h) => Number(h))
    .filter((h) => Number.isInteger(h) && h >= 1 && h <= 36);
}

// ---- compile-time parity with the engine contract (fails typecheck if they drift) ----
type Assignable<A, B> = [A] extends [B] ? true : false;
const _f: Assignable<FormatConfig, EngineFormatConfig> = true;
const _h: Assignable<HandicapConfig, EngineHandicapConfig> = true;
const _t: Assignable<TiebreakConfig, EngineTiebreakConfig> = true;
const _sp: Assignable<StrokePlayRules, EngineStrokePlayRules> = true;
const _s: Assignable<ScoringConfig, EngineScoringConfig> = true;
void _f;
void _h;
void _t;
void _sp;
void _s;
